import React, { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Copy, RefreshCw, Eye, EyeOff, Info, Check, Plus, Trash2, Save, SquareArrowOutUpRight } from 'lucide-react'
import { FaTimes } from 'react-icons/fa'
import UnauthorizedPage from '../../layouts/UnauthorizedPage'
import server from "../../../api/server"
import { useToast } from '../../../context/ToastContext'
import ModernTimeDropdown from '../../layouts/modernDropdown'
import ClientEventsConfiguration from './ClientEventsConfiguration'

const MCP_FEATURE_DISABLED_MESSAGE = 'Please contact Anthropod Team for enabling MCP access.'
const MCP_SETUP_GUIDE_URL = 'https://docs.google.com/document/d/1Mdr1Fa4Y0KE4rFFDPNVmrNdgBkA17lnah6sXxmYg2TQ/edit?usp=sharing'
const DEVELOPER_HUB_TABS = [
  { id: 'callbacks', label: 'Callbacks' },
  { id: 'api_keys', label: 'API Keys' },
  { id: 'mcp', label: 'MCP' },
  { id: 'events', label: 'Events' },
  { id: 'widget', label: 'Widget' }
]

const WIDGET_ANALYTICS_FIELD_GROUPS = [
  {
    label: 'Identifiers',
    fields: [
      { key: 'id', label: 'Record ID', required: false, description: 'Uses the generated conversation ID when not mapped.' },
      { key: 'customer_id', label: 'Customer / Patient ID', required: true },
      { key: 'employee_id', label: 'Employee / Doctor ID', required: true }
    ]
  },
  {
    label: 'CRM metadata',
    fields: [
      { key: 'call_direction', label: 'Call Direction', required: false },
      { key: 'customer_phone_number', label: 'Customer Phone Number', required: false },
      { key: 'lead_source', label: 'Lead Source', required: false },
      { key: 'lead_sub_source', label: 'Lead Sub-source', required: false },
      { key: 'crm_location', label: 'CRM Location', required: false },
      { key: 'crm_lead_stage', label: 'CRM Lead Stage', required: false },
      { key: 'crm_lead_sub_stage', label: 'CRM Lead Sub-stage', required: false },
      { key: 'crm_followup_time', label: 'CRM Follow-up Time', required: false },
      { key: 'crm_lead_created_at', label: 'CRM Lead Created At', required: false },
      { key: 'campaign_id', label: 'Campaign ID', required: false },
      { key: 'campaign_batch_id', label: 'Campaign Batch ID', required: false }
    ]
  },
  {
    label: 'Tags',
    fields: [
      { key: 'tag', label: 'Tag 1', required: false },
      { key: 'tag2', label: 'Tag 2', required: false },
      { key: 'tag3', label: 'Tag 3', required: false },
      { key: 'tag4', label: 'Tag 4', required: false },
      { key: 'tag5', label: 'Tag 5', required: false },
      { key: 'tag6', label: 'Tag 6', required: false },
      { key: 'tag7', label: 'Tag 7', required: false },
      { key: 'tag8', label: 'Tag 8', required: false },
      { key: 'tag9', label: 'Tag 9', required: false },
      { key: 'tag10', label: 'Tag 10', required: false },
      { key: 'tag11', label: 'Tag 11', required: false },
      { key: 'tag12', label: 'Tag 12', required: false },
      { key: 'tag13', label: 'Tag 13', required: false },
      { key: 'tag14', label: 'Tag 14', required: false },
      { key: 'tag15', label: 'Tag 15', required: false },
      { key: 'tag16', label: 'Tag 16', required: false },
      { key: 'tag17', label: 'Tag 17', required: false },
      { key: 'tag18', label: 'Tag 18', required: false },
      { key: 'tag19', label: 'Tag 19', required: false },
      { key: 'tag20', label: 'Tag 20', required: false },
      { key: 'tag21', label: 'Tag 21', required: false },
      { key: 'tag22', label: 'Tag 22', required: false },
      { key: 'tag23', label: 'Tag 23', required: false },
      { key: 'tag24', label: 'Tag 24', required: false },
      { key: 'tag25', label: 'Tag 25', required: false },
      { key: 'tag26', label: 'Tag 26', required: false },
      { key: 'tag27', label: 'Tag 27', required: false },
      { key: 'tag28', label: 'Tag 28', required: false },
      { key: 'tag29', label: 'Tag 29', required: false },
      { key: 'tag30', label: 'Tag 30', required: false }
    ]
  }
]

const WIDGET_ANALYTICS_FIELDS = WIDGET_ANALYTICS_FIELD_GROUPS.flatMap(group => group.fields)

const createDefaultAnalyticsFieldMapping = () => WIDGET_ANALYTICS_FIELDS.reduce((mapping, field) => ({
  ...mapping,
  [field.key]: ''
}), {})

const createDefaultWidgetField = () => ({
  id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  type: 'text',
  required: false
})

const createDefaultWidgetWebhook = () => ({
  id: `webhook_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  webhook_url: '',
  webhook_header: '',
  headers: ''
})

const normalizeWebhookHeaders = (headers) => {
  if(!headers){
    return ''
  }
  if(typeof headers === 'string'){
    return headers
  }
  if(typeof headers === 'object'){
    return Object.entries(headers)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ')
  }
  return String(headers)
}

const ALL_CALLBACK_TARGET = 'all'

const createDefaultCallback = (targetId = ALL_CALLBACK_TARGET) => {
  const callbackId = `callback_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
  return {
    id: callbackId,
    callback_id: callbackId,
    url: '',
    header: '',
    target_ids: [targetId]
  }
}

const normalizeCallbackTargetId = (value) => {
  if(value === undefined || value === null || value === ''){
    return ALL_CALLBACK_TARGET
  }
  return String(value)
}

const normalizeCallbackTargetIds = (...values) => {
  const normalized = []
  const seen = new Set()

  values.flatMap(value => Array.isArray(value) ? value : [value]).forEach(value => {
    if(value === undefined || value === null || value === ''){
      return
    }
    const normalizedValue = normalizeCallbackTargetId(value)
    if(seen.has(normalizedValue)){
      return
    }
    seen.add(normalizedValue)
    normalized.push(normalizedValue)
  })

  if(normalized.length === 0 || normalized.includes(ALL_CALLBACK_TARGET)){
    return [ALL_CALLBACK_TARGET]
  }
  return normalized
}

const getCallbackTargetIds = (callback) => {
  if(Array.isArray(callback?.target_ids)){
    return normalizeCallbackTargetIds(callback.target_ids)
  }
  return normalizeCallbackTargetIds(callback?.target_id)
}

const normalizeCallbackConfigurations = (analyticsConfig) => {
  const incomingCallbacks = Array.isArray(analyticsConfig)
    ? analyticsConfig
    : analyticsConfig?.callbacks
      || analyticsConfig?.callback_configurations
      || analyticsConfig?.analytics_callbacks
      || analyticsConfig?.callback_urls

  if(Array.isArray(incomingCallbacks)){
    return incomingCallbacks.length > 0
      ? incomingCallbacks.map((callback, index) => ({
        id: callback.callback_id || callback.id || `callback_${index}_${Date.now()}`,
        callback_id: callback.callback_id || callback.id || null,
        url: callback.callback_url || callback.analytics_url || callback.url || '',
        header: normalizeWebhookHeaders(callback.callback_header || callback.analytics_header || callback.header),
        target_ids: normalizeCallbackTargetIds(
          callback.target_ids,
          callback.service_ids,
          callback.agent_ids,
          callback.target_id,
          callback.service_id,
          callback.agent_id,
          callback.serviceIds,
          callback.agentIds,
          callback.serviceId,
          callback.agentId
        )
      }))
      : [createDefaultCallback()]
  }

  const legacyUrl = analyticsConfig?.callback_url || analyticsConfig?.analytics_url || analyticsConfig?.url || ''
  const legacyHeader = normalizeWebhookHeaders(analyticsConfig?.callback_header || analyticsConfig?.analytics_header || analyticsConfig?.header)
  const legacyTargetIds = normalizeCallbackTargetIds(
    analyticsConfig?.target_ids,
    analyticsConfig?.service_ids,
    analyticsConfig?.agent_ids,
    analyticsConfig?.target_id,
    analyticsConfig?.service_id,
    analyticsConfig?.agent_id
  )
  if(legacyUrl || legacyHeader || legacyTargetIds.some(targetId => targetId !== ALL_CALLBACK_TARGET)){
    return [{
      ...createDefaultCallback(),
      url: legacyUrl,
      header: legacyHeader,
      target_ids: legacyTargetIds
    }]
  }

  return [createDefaultCallback()]
}

const normalizeIdOptions = (items, { useValueAsLabel = false } = {}) => {
  const normalized = []
  const seen = new Set()

  ;(Array.isArray(items) ? items : []).forEach(item => {
    const value = typeof item === 'string' || typeof item === 'number'
      ? item
      : item?.service_id || item?.serviceId || item?.agent_id || item?.agentId || item?.id || item?.value
    if(value === undefined || value === null || value === ''){
      return
    }
    const valueString = String(value)
    if(seen.has(valueString)){
      return
    }
    seen.add(valueString)
    normalized.push({
      value: valueString,
      label: useValueAsLabel
        ? valueString
        : typeof item === 'object'
        ? item.label || item.name || item.agent_name || item.agentName || valueString
        : valueString
    })
  })

  return normalized
}

const createDefaultWidget = () => ({
  id: `widget_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title: 'New Call Recording Widget',
  source_type: 'url',
  source_url: '',
  source_header: '',
  webhook_url: '',
  webhook_header: '',
  widget_webhooks: [createDefaultWidgetWebhook()],
  max_duration_seconds: 300,
  fields: [createDefaultWidgetField()],
  send_to_call_analytics: false,
  analytics_service_id: '',
  analytics_field_mapping: createDefaultAnalyticsFieldMapping(),
  active: true
})

const normalizeWidget = (widget, index = 0) => {
  const content = widget?.content || {}
  const incomingWidgetWebhooks = content.widget_webhooks || widget?.widget_webhooks || content.webhooks || widget?.webhooks
  const legacyWebhookUrl = content.webhook_url || widget?.webhook_url || ''
  const legacyWebhookHeader = content.webhook_header || widget?.webhook_header || content.webhook_headers || widget?.webhook_headers || ''
  const normalizedWebhooks = Array.isArray(incomingWidgetWebhooks) && incomingWidgetWebhooks.length > 0
    ? incomingWidgetWebhooks.map((webhook, webhookIndex) => ({
      id: webhook.id || `webhook_${index}_${webhookIndex}_${Date.now()}`,
      webhook_url: webhook.webhook_url || webhook.url || '',
      webhook_header: normalizeWebhookHeaders(webhook.webhook_header || webhook.webhook_headers || webhook.headers || webhook.header),
      headers: normalizeWebhookHeaders(webhook.headers || webhook.webhook_header || webhook.webhook_headers || webhook.header)
    }))
    : [{
      id: `webhook_${index}_legacy_${Date.now()}`,
      webhook_url: legacyWebhookUrl,
      webhook_header: legacyWebhookHeader,
      headers: legacyWebhookHeader
    }]
  const mergedWidget = {
    ...content,
    ...widget,
    fields: content.fields || widget?.fields,
    id: content.id || widget?.id,
    widget_id: widget?.widget_id || content.widget_id || content.id || widget?.id,
    widget_title: widget?.widget_title || content.widget_title,
    title: content.title || widget?.title,
    updated_by: widget?.updated_by || content.updated_by,
    updated_at: widget?.updated_at || content.updated_at
  }

  return {
    id: mergedWidget.id || mergedWidget.widget_id || `widget_${index}_${Date.now()}`,
    widget_id: mergedWidget.widget_id || mergedWidget.id || null,
    title: mergedWidget.title || mergedWidget.widget_title || `Call Recording Widget ${index + 1}`,
    updated_by: mergedWidget.updated_by || mergedWidget.updatedBy || mergedWidget.updated_by_email || '-',
    updated_at: mergedWidget.updated_at || mergedWidget.updatedAt || mergedWidget.updated_at_epoch || null,
    source_type: (mergedWidget.source_type === 'manual' || mergedWidget.source_type === 'custom') ? 'manual' : 'url',
    source_url: mergedWidget.source_url || mergedWidget.url_config?.url || '',
    source_header: mergedWidget.source_header || mergedWidget.configuration_header || mergedWidget.url_config?.header || '',
    webhook_url: normalizedWebhooks[0]?.webhook_url || '',
    webhook_header: normalizedWebhooks[0]?.webhook_header || '',
    widget_webhooks: normalizedWebhooks,
    max_duration_seconds: Number(mergedWidget.max_duration_seconds || mergedWidget.max_duration || 300),
    fields: Array.isArray(mergedWidget.fields) && mergedWidget.fields.length > 0
      ? mergedWidget.fields.map((field, fieldIndex) => ({
        id: field.id || `field_${index}_${fieldIndex}_${Date.now()}`,
        name: field.name || field.label || field.key || '',
        type: field.type || 'text',
        required: Boolean(field.required)
      }))
      : [createDefaultWidgetField()],
    send_to_call_analytics: Boolean(mergedWidget.send_to_call_analytics),
    analytics_service_id: mergedWidget.analytics_service_id || '',
    analytics_field_mapping: {
      ...createDefaultAnalyticsFieldMapping(),
      ...(mergedWidget.analytics_field_mapping || {})
    },
    active: mergedWidget.active !== false && mergedWidget.status !== 'inactive'
  }
}

const formatWidgetUpdatedAt = (value) => {
  if(!value){
    return '-'
  }
  const numericValue = Number(value)
  const date = Number.isNaN(numericValue)
    ? new Date(value)
    : new Date(numericValue < 10000000000 ? numericValue * 1000 : numericValue)

  if(Number.isNaN(date.getTime())){
    return '-'
  }

  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

const DeveloperHubConfiguration = ({serviceID, clientID}) => {
    const { showToast } = useToast();
  const [privateApiKey, setPrivateApiKey] = useState(null)
  const [publicApiKey, setPublicApiKey] = useState(null)
  const [chatExpanded, setChatExpanded] = useState(false)
  const [callExpanded, setCallExpanded] = useState(false)
  const [client_id, setClient_id] = useState('')
  const [service_id, setService_id] = useState('')
  const [email, setEmail] = useState('')
  const [loadingPrivateApiKey, setLoadingPrivateApiKey] = useState(false)
  const [loadingPublicApiKey, setLoadingPublicApiKey] = useState(false)
  const [showHeaderInfoChat, setShowHeaderInfoChat] = useState(false)
  const [showHeaderInfoCall, setShowHeaderInfoCall] = useState(false)
  const [voiceAgentsExpanded, setVoiceAgentsExpanded] = useState(false)
  const [showHeaderInfoVoiceAgents, setShowHeaderInfoVoiceAgents] = useState(false)
  const [showCopiedPrivateApiKey, setShowCopiedPrivateApiKey] = useState(false)
  const [showCopiedPublicApiKey, setShowCopiedPublicApiKey] = useState(false)
  const [showPrivateApiKey, setShowPrivateApiKey] = useState(false)
  const [showPublicApiKey, setShowPublicApiKey] = useState(false)
  const [mcpEnabled, setMcpEnabled] = useState(false)
  const [mcpClientId, setMcpClientId] = useState(null)
  const [mcpClientSecret, setMcpClientSecret] = useState(null)
  const [mcpServerUrl, setMcpServerUrl] = useState(null)
  const [mcpFeatureEnabled, setMcpFeatureEnabled] = useState(false)
  const [mcpPermanentFeatureEnabled, setMcpPermanentFeatureEnabled] = useState(false)
  const [mcpTrialAccessEnabled, setMcpTrialAccessEnabled] = useState(false)
  const [mcpTrialAccessActive, setMcpTrialAccessActive] = useState(false)
  const [mcpTrialAccessExpiresAt, setMcpTrialAccessExpiresAt] = useState(null)
  const [activeTab, setActiveTab] = useState('callbacks')
  const [widgets, setWidgets] = useState([])
  const [widgetForm, setWidgetForm] = useState(createDefaultWidget())
  const [widgetModalOpen, setWidgetModalOpen] = useState(false)
  const [widgetModalMode, setWidgetModalMode] = useState('create')
  const [loadingWidgets, setLoadingWidgets] = useState(false)
  const [loadingWidgetDetails, setLoadingWidgetDetails] = useState(false)
  const [widgetToDelete, setWidgetToDelete] = useState(null)
  const [deletingWidgetId, setDeletingWidgetId] = useState(null)
  const [savingWidgetConfiguration, setSavingWidgetConfiguration] = useState(false)
  const [loadingMcpAccess, setLoadingMcpAccess] = useState(false)
  const [generatingMcpCredential, setGeneratingMcpCredential] = useState(false)
  const [rotatingMcpSecret, setRotatingMcpSecret] = useState(false)
  const [showMcpClientSecret, setShowMcpClientSecret] = useState(false)
  const [showCopiedMcpClientId, setShowCopiedMcpClientId] = useState(false)
  const [showCopiedMcpClientSecret, setShowCopiedMcpClientSecret] = useState(false)
  const [showCopiedMcpServerUrl, setShowCopiedMcpServerUrl] = useState(false)
    function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }

  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })

  const [chatAnalytics, setChatAnalytics] = useState([createDefaultCallback()])
  const [callAnalytics, setCallAnalytics] = useState([createDefaultCallback()])
  const [voiceAgentsAnalytics, setVoiceAgentsAnalytics] = useState([createDefaultCallback()])
  const [serviceIdOptions, setServiceIdOptions] = useState([])
  const [voiceAgentOptions, setVoiceAgentOptions] = useState([])
  const [loadingVoiceAgentOptions, setLoadingVoiceAgentOptions] = useState(false)
  const [callbackModalOpen, setCallbackModalOpen] = useState(false)
  const [callbackModalType, setCallbackModalType] = useState(null)
  const [callbackModalMode, setCallbackModalMode] = useState('create')
  const [callbackForm, setCallbackForm] = useState(createDefaultCallback())
  const [savingCallbackConfiguration, setSavingCallbackConfiguration] = useState(false)
  const [callbackToDelete, setCallbackToDelete] = useState(null)
  const [deletingCallbackConfiguration, setDeletingCallbackConfiguration] = useState(false)

  const generateNewPrivateApiKey = async () => {
    setLoadingPrivateApiKey(true)
    let res = await server.generateNewApiKey({client_id, service_id, email,menu: 'developer-hub', submenu: 'configuration', 'generate_type': 'private'})
    let resp = await res.json();
    if(res.status === 200){
      setPrivateApiKey(resp.api_key);
      showToast('New API key generated successfully', 'success')
      setLoadingPrivateApiKey(false)
    }else{
      showToast(resp.error, 'error')
      setLoadingPrivateApiKey(false)
    }
  }

  const generateNewPublicApiKey = async () => {
    setLoadingPublicApiKey(true)
    let res = await server.generateNewApiKey({client_id, service_id, email,menu: 'developer-hub', submenu: 'configuration', 'generate_type': 'public'})
    let resp = await res.json();
    if(res.status === 200){
      setPublicApiKey(resp.api_key);
      showToast('New API key generated successfully', 'success')
      setLoadingPublicApiKey(false)
    }else{
      showToast(resp.error, 'error')
      setLoadingPublicApiKey(false)
    }
  }

  const copyToClipboardPublicApiKey = (text) => {
    setShowCopiedPublicApiKey(true)
    navigator.clipboard.writeText(text)
    setTimeout(()=>{
      setShowCopiedPublicApiKey(false)
    }, 2000)
  }

  const copyToClipboardPrivateApiKey = (text) => {
    setShowCopiedPrivateApiKey(true)
    navigator.clipboard.writeText(text)
    setTimeout(()=>{
      setShowCopiedPrivateApiKey(false)
    }, 2000)
  }
  const copyToClipboardMcpServerUrl = (text) => {
    setShowCopiedMcpServerUrl(true)
    navigator.clipboard.writeText(text)
    setTimeout(()=>{
      setShowCopiedMcpServerUrl(false)
    }, 2000)
  }

  const copyToClipboardMcpClientId = (text) => {
    setShowCopiedMcpClientId(true)
    navigator.clipboard.writeText(text)
    setTimeout(()=>{
      setShowCopiedMcpClientId(false)
    }, 2000)
  }

  const copyToClipboardMcpClientSecret = (text) => {
    setShowCopiedMcpClientSecret(true)
    navigator.clipboard.writeText(text)
    setTimeout(()=>{
      setShowCopiedMcpClientSecret(false)
    }, 2000)
  }

  const hydrateMcpConfiguration = (data) => {
    setMcpFeatureEnabled(Boolean(data?.is_mcp_feature_enabled ?? data?.mcp_feature_enabled))
    setMcpPermanentFeatureEnabled(Boolean(data?.is_mcp_feature_permanently_enabled))
    setMcpTrialAccessEnabled(Boolean(data?.is_mcp_trial_access_enabled))
    setMcpTrialAccessActive(Boolean(data?.is_mcp_trial_access_active))
    setMcpTrialAccessExpiresAt(data?.mcp_trial_access_expires_at || null)
    setMcpEnabled(Boolean(data?.enabled))
    setMcpClientId(data?.mcp_client_id || null)
    setMcpClientSecret(data?.mcp_client_secret || null)
    setMcpServerUrl(data?.mcp_server_url || null)
  }

  const getMcpConfiguration = useCallback(async (client_id, service_id, email) => {
    try {
      setLoadingMcpAccess(true)
      let res = await server.getMcpConfiguration(client_id, service_id, email, 'developer-hub', 'configuration')
      let resp = await res.json();
      if(res.status === 200){
        hydrateMcpConfiguration(resp.data)
      }else{
        showToast(resp.error || 'Unable to fetch MCP configuration', 'error')
      }
      setLoadingMcpAccess(false)
    } catch (error) {
      setLoadingMcpAccess(false)
    }
  }, [showToast])

  const updateMcpEnabled = async () => {
    if(!mcpFeatureEnabled){
      showToast(MCP_FEATURE_DISABLED_MESSAGE, 'error')
      return
    }
    try {
      setLoadingMcpAccess(true)
      let res = await server.updateMcpEnable({client_id, service_id, email, menu: 'developer-hub', submenu: 'configuration', enabled: !mcpEnabled})
      let resp = await res.json();
      if(res.status === 200){
        setMcpEnabled(Boolean(resp.data.enabled))
        showToast(resp.data.enabled ? 'MCP access enabled' : 'MCP access disabled', 'success')
      }else{
        showToast(resp.error || 'Unable to update MCP access', 'error')
      }
      setLoadingMcpAccess(false)
    } catch (error) {
      setLoadingMcpAccess(false)
    }
  }

  const generateMcpCredential = async () => {
    if(!mcpFeatureEnabled){
      showToast(MCP_FEATURE_DISABLED_MESSAGE, 'error')
      return
    }
    try {
      setGeneratingMcpCredential(true)
      let res = await server.generateMcpCredential({client_id, service_id, email, credential_name: 'Default MCP credential', menu: 'developer-hub', submenu: 'configuration'})
      let resp = await res.json();
      if(res.status === 200){
        hydrateMcpConfiguration(resp.data)
        showToast('MCP credential generated successfully', 'success')
      }else{
        showToast(resp.error || 'Unable to generate MCP credential', 'error')
      }
      setGeneratingMcpCredential(false)
    } catch (error) {
      setGeneratingMcpCredential(false)
    }
  }

  const rotateMcpSecret = async () => {
    if(!mcpFeatureEnabled){
      showToast(MCP_FEATURE_DISABLED_MESSAGE, 'error')
      return
    }
    if(!mcpClientId){
      generateMcpCredential()
      return
    }
    try {
      setRotatingMcpSecret(true)
      let res = await server.rotateMcpSecret({client_id, service_id, email, mcp_client_id: mcpClientId, menu: 'developer-hub', submenu: 'configuration'})
      let resp = await res.json();
      if(res.status === 200){
        setMcpClientSecret(resp.data.mcp_client_secret)
        showToast('MCP secret rotated successfully', 'success')
      }else{
        showToast(resp.error || 'Unable to rotate MCP secret', 'error')
      }
      setRotatingMcpSecret(false)
    } catch (error) {
      setRotatingMcpSecret(false)
    }
  }
  const getAnalyticsStateHelpers = (type) => {
    if(type === 'call'){
      return { callbacks: callAnalytics, setCallbacks: setCallAnalytics }
    }
    if(type === 'chat'){
      return { callbacks: chatAnalytics, setCallbacks: setChatAnalytics }
    }
    return { callbacks: voiceAgentsAnalytics, setCallbacks: setVoiceAgentsAnalytics }
  }

  const openCreateCallbackModal = (type) => {
    setCallbackModalType(type)
    setCallbackModalMode('create')
    setCallbackForm(createDefaultCallback())
    setCallbackModalOpen(true)
  }

  const openEditCallbackModal = (type, callback) => {
    setCallbackModalType(type)
    setCallbackModalMode('edit')
    setCallbackForm({
      ...createDefaultCallback(),
      ...callback,
      header: normalizeWebhookHeaders(callback.header),
      target_ids: getCallbackTargetIds(callback),
      target_id: undefined
    })
    setCallbackModalOpen(true)
  }

  const closeCallbackModal = () => {
    setCallbackModalOpen(false)
    setCallbackModalType(null)
    setCallbackModalMode('create')
    setCallbackForm(createDefaultCallback())
    setSavingCallbackConfiguration(false)
  }

  const updateCallbackForm = (field, value) => {
    setCallbackForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const updateCallbackTargetIds = (targetIds) => {
    setCallbackForm(prev => ({
      ...prev,
      target_ids: targetIds,
      target_id: undefined
    }))
  }

  const updateCallbackTargetSelection = (selectedValues) => {
    if(!selectedValues.length){
      updateCallbackTargetIds([ALL_CALLBACK_TARGET])
      return
    }
    const nextTargetIds = normalizeCallbackTargetIds(selectedValues)
    const currentTargetIds = getCallbackTargetIds(callbackForm)
    const hadAllSelected = currentTargetIds.includes(ALL_CALLBACK_TARGET)
    const hasAllSelected = nextTargetIds.includes(ALL_CALLBACK_TARGET)

    if(hasAllSelected && nextTargetIds.length === 1){
      updateCallbackTargetIds([ALL_CALLBACK_TARGET])
      return
    }
    if(hasAllSelected && !hadAllSelected){
      updateCallbackTargetIds([ALL_CALLBACK_TARGET])
      return
    }
    updateCallbackTargetIds(nextTargetIds.filter(targetId => targetId !== ALL_CALLBACK_TARGET))
  }

  const getCallbackTargetDropdownOptions = (type) => (
    callbackTargetOptions[type]?.map(option => option.label) || []
  )

  const getCallbackTargetDropdownValue = (type, callback) => {
    const targetIds = getCallbackTargetIds(callback)
    return targetIds.map(targetId => {
      const option = callbackTargetOptions[type]?.find(option => option.value === targetId)
      return option?.label || targetId
    })
  }

  const handleCallbackTargetDropdownChange = (type, selectedLabels) => {
    const labels = Array.isArray(selectedLabels) ? selectedLabels : [selectedLabels]
    const selectedValues = labels.map(label => {
      const option = callbackTargetOptions[type]?.find(option => option.label === label)
      return option?.value || label
    })
    updateCallbackTargetSelection(selectedValues)
  }

  const saveCallbackModalConfiguration = async () => {
    if(!callbackModalType){
      return
    }
    if(!callbackForm.url?.trim()){
      showToast('Callback URL is required.', 'error')
      return
    }
    if(!isValidUrl(callbackForm.url.trim())){
      showToast('Callback URL is not valid. Please enter a valid URL.', 'error')
      return
    }

    const { callbacks, setCallbacks } = getAnalyticsStateHelpers(callbackModalType)
    const sanitizedForm = {
      ...callbackForm,
      url: callbackForm.url.trim(),
      header: normalizeWebhookHeaders(callbackForm.header).trim(),
      target_ids: getCallbackTargetIds(callbackForm),
      target_id: undefined
    }
    const nonEmptyCallbacks = callbacks.filter(callback => callback.url?.trim() || normalizeWebhookHeaders(callback.header).trim())
    const nextCallbacks = callbackModalMode === 'edit'
      ? callbacks.map(callback => callback.id === sanitizedForm.id ? sanitizedForm : callback)
      : [...nonEmptyCallbacks, sanitizedForm]

    try {
      setSavingCallbackConfiguration(true)
      const saved = await saveDeveloperHubConfiguration(callbackModalType, nextCallbacks)
      setSavingCallbackConfiguration(false)
      if(saved){
        setCallbacks(nextCallbacks.length > 0 ? nextCallbacks : [createDefaultCallback()])
        closeCallbackModal()
      }
    } catch (error) {
      setSavingCallbackConfiguration(false)
      showToast('Unable to save callback configuration', 'error')
    }
  }

  const openDeleteCallbackConfirmation = (type, callback) => {
    setCallbackToDelete({ type, callback })
  }

  const deleteCallbackConfiguration = async () => {
    if(!callbackToDelete){
      return
    }
    const { type, callback } = callbackToDelete
    const { callbacks, setCallbacks } = getAnalyticsStateHelpers(type)
    const nextCallbacks = callbacks.filter(existingCallback => existingCallback.id !== callback.id)
    const callbacksToPersist = nextCallbacks.filter(callback => callback.url?.trim() || normalizeWebhookHeaders(callback.header).trim())
    setDeletingCallbackConfiguration(true)
    const saved = await saveDeveloperHubConfiguration(type, callbacksToPersist)
    setDeletingCallbackConfiguration(false)
    if(saved){
      setCallbacks(nextCallbacks.length > 0 ? nextCallbacks : [createDefaultCallback()])
      setCallbackToDelete(null)
    }
  }

  const normalizeWidgets = (incomingWidgets) => {
    if(!Array.isArray(incomingWidgets) || incomingWidgets.length === 0){
      return []
    }
    return incomingWidgets.map((widget, index) => normalizeWidget(widget, index))
  }

  const updateWidgetForm = (field, value) => {
    setWidgetForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const updateWidgetFormField = (fieldId, key, value) => {
    setWidgetForm(prev => ({
      ...prev,
      fields: prev.fields.map(field => field.id === fieldId ? {
        ...field,
        [key]: value
      } : field)
    }))
  }

  const updateWidgetAnalyticsMapping = (field, value) => {
    setWidgetForm(prev => ({
      ...prev,
      analytics_field_mapping: {
        ...prev.analytics_field_mapping,
        [field]: value
      }
    }))
  }

  const addWidgetFormField = () => {
    setWidgetForm(prev => ({
      ...prev,
      fields: [...prev.fields, createDefaultWidgetField()]
    }))
  }

  const removeWidgetFormField = (fieldId) => {
    setWidgetForm(prev => {
      if(prev.fields.length === 1){
        return prev
      }
      return {
        ...prev,
        fields: prev.fields.filter(field => field.id !== fieldId)
      }
    })
  }

  const updateWidgetFormWebhook = (webhookId, key, value) => {
    setWidgetForm(prev => ({
      ...prev,
      widget_webhooks: prev.widget_webhooks.map(webhook => webhook.id === webhookId ? {
        ...webhook,
        [key]: value,
        ...(key === 'webhook_header' ? { headers: value } : {})
      } : webhook)
    }))
  }

  const addWidgetFormWebhook = () => {
    setWidgetForm(prev => ({
      ...prev,
      widget_webhooks: [...prev.widget_webhooks, createDefaultWidgetWebhook()]
    }))
  }

  const removeWidgetFormWebhook = (webhookId) => {
    setWidgetForm(prev => {
      if(prev.widget_webhooks.length === 1){
        return prev
      }
      return {
        ...prev,
        widget_webhooks: prev.widget_webhooks.filter(webhook => webhook.id !== webhookId)
      }
    })
  }

  const validateWidgetConfiguration = (widget) => {
    if(!widget.title?.trim()){
      return 'Widget title is required.'
    }
    if(widget.source_type === 'url' && !widget.source_url?.trim()){
      return 'Configuration URL is required for URL based widgets.'
    }
    if(widget.source_url?.trim() && !isValidUrl(widget.source_url?.trim())){
      return 'Configuration URL is not valid.'
    }
    const widgetWebhooks = Array.isArray(widget.widget_webhooks) ? widget.widget_webhooks : []
    const hasIncompleteWebhook = widgetWebhooks.some(webhook => !webhook.webhook_url?.trim() && (webhook.webhook_header?.trim() || webhook.headers?.trim()))
    if(hasIncompleteWebhook){
      return 'Webhook URL is required when webhook headers are added.'
    }
    const invalidWebhookIndex = widgetWebhooks.findIndex(webhook => webhook.webhook_url?.trim() && !isValidUrl(webhook.webhook_url?.trim()))
    if(invalidWebhookIndex !== -1){
      return `Webhook URL ${invalidWebhookIndex + 1} is not valid.`
    }
    if(!Number(widget.max_duration_seconds) || Number(widget.max_duration_seconds) <= 0){
      return 'Max duration must be greater than 0 seconds.'
    }
    const validFields = widget.fields.filter(field => field.name?.trim())
    if(validFields.length === 0){
      return 'Add at least one expected field.'
    }
    const hasIncompleteField = widget.fields.some(field => field.name?.trim())
      && widget.fields.some(field => !field.name?.trim())
    if(hasIncompleteField){
      return 'Complete or remove incomplete expected fields.'
    }
    if(widget.send_to_call_analytics){
      if(!String(widget.analytics_service_id || '').trim()){
        return 'Select a Conversation Analytics project.'
      }
      const fieldNames = new Set(validFields.map(field => field.name.trim()))
      const requiredMapping = WIDGET_ANALYTICS_FIELDS.find(field => field.required && !String(widget.analytics_field_mapping?.[field.key] || '').trim())
      if(requiredMapping){
        return `Map ${requiredMapping.label} before enabling Conversation Analytics.`
      }
      const invalidMapping = WIDGET_ANALYTICS_FIELDS.find(field => {
        const sourceField = String(widget.analytics_field_mapping?.[field.key] || '').trim()
        return sourceField && !fieldNames.has(sourceField)
      })
      if(invalidMapping){
        return `${invalidMapping.label} must map to an expected field.`
      }
    }
    return null
  }

  const sanitizeWidgetConfiguration = (widget) => {
    const widgetWebhooks = (Array.isArray(widget.widget_webhooks) ? widget.widget_webhooks : [])
      .filter(webhook => webhook.webhook_url?.trim())
      .map(webhook => ({
        ...webhook,
        webhook_url: webhook.webhook_url.trim(),
        webhook_header: (webhook.webhook_header || webhook.headers)?.trim() || '',
        headers: (webhook.headers || webhook.webhook_header)?.trim() || ''
      }))
    const primaryWebhook = widgetWebhooks[0] || { webhook_url: '', webhook_header: '' }

    return {
      ...widget,
      widget_id: widget.widget_id || widget.id,
      widget_title: widget.title.trim(),
      title: widget.title.trim(),
      source_url: widget.source_url?.trim() || '',
      source_header: widget.source_header?.trim() || '',
      webhook_url: primaryWebhook.webhook_url,
      webhook_header: primaryWebhook.webhook_header,
      widget_webhooks: widgetWebhooks,
      max_duration_seconds: Number(widget.max_duration_seconds),
      send_to_call_analytics: Boolean(widget.send_to_call_analytics),
      analytics_service_id: widget.send_to_call_analytics ? String(widget.analytics_service_id || '').trim() : '',
      analytics_field_mapping: WIDGET_ANALYTICS_FIELDS.reduce((mapping, field) => ({
        ...mapping,
        [field.key]: String(widget.analytics_field_mapping?.[field.key] || '').trim()
      }), {}),
      fields: widget.fields
        .filter(field => field.name?.trim())
        .map(field => ({
          ...field,
          name: field.name.trim()
        }))
    }
  }

  const getWidgetConfigurations = useCallback(async (client_id, service_id, email) => {
    try {
      setLoadingWidgets(true)
      let res = await server.getDeveloperHubWidgetConfigurations(client_id, service_id, email, 'developer-hub', 'configuration')
      let resp = await res.json();
      if(res.status === 200){
        setWidgets(normalizeWidgets(resp.data?.widgets || resp.data?.widget_configurations || resp.data || []))
      }else{
        showToast(resp.error || 'Unable to fetch widget configurations', 'error')
      }
      setLoadingWidgets(false)
    } catch (error) {
      setLoadingWidgets(false)
      showToast('Unable to fetch widget configurations', 'error')
    }
  }, [showToast])

  const openCreateWidgetModal = () => {
    setWidgetForm(createDefaultWidget())
    setWidgetModalMode('create')
    setWidgetModalOpen(true)
  }

  const openEditWidgetModal = async (widget) => {
    setWidgetModalMode('edit')
    setWidgetModalOpen(true)
    setWidgetForm(normalizeWidget(widget))
    const widgetId = widget.widget_id || widget.id
    if(!widgetId){
      return
    }
    try {
      setLoadingWidgetDetails(true)
      let res = await server.getDeveloperHubWidgetDetails(client_id, service_id, email, widgetId, 'developer-hub', 'configuration')
      let resp = await res.json();
      if(res.status === 200){
        setWidgetForm(normalizeWidget(resp.data?.widget || resp.data?.widget_configuration || resp.data || widget))
      }else{
        showToast(resp.error || 'Unable to fetch widget details', 'error')
      }
      setLoadingWidgetDetails(false)
    } catch (error) {
      setLoadingWidgetDetails(false)
      showToast('Unable to fetch widget details', 'error')
    }
  }

  const closeWidgetModal = () => {
    setWidgetModalOpen(false)
    setLoadingWidgetDetails(false)
    setSavingWidgetConfiguration(false)
  }

  const getServiceIdOptions = useCallback(async (client_id, service_id, email) => {
    const fallbackOptions = normalizeIdOptions(Array.isArray(serviceID) ? serviceID : [serviceID || service_id])
    try {
      let res = await server.getProjectsList(client_id, service_id || 'DEFAULT', email, 'developer-hub', 'configuration', role)
      if(res.status === 200){
        let resp = await res.json()
        const projectOptions = normalizeIdOptions(resp.data || [])
        setServiceIdOptions(projectOptions.length > 0 ? projectOptions : fallbackOptions)
      }else{
        setServiceIdOptions(fallbackOptions)
      }
    } catch (error) {
      setServiceIdOptions(fallbackOptions)
    }
  }, [role, serviceID])

  const getVoiceAgentOptions = useCallback(async (client_id, service_id, email) => {
    try {
      setLoadingVoiceAgentOptions(true)
      let res = await server.getAgentsList(client_id, '', email, 'developer-hub', 'configuration')
      if(res.status === 200){
        let resp = await res.json()
        setVoiceAgentOptions(normalizeIdOptions(resp.data || [], { useValueAsLabel: true }))
      }
      setLoadingVoiceAgentOptions(false)
    } catch (error) {
      setLoadingVoiceAgentOptions(false)
    }
  }, [])

  const getDeveloperHubConfiguration = async (client_id, service_id, email) => {
    try {
        setLoadingPrivateApiKey(true)
        setLoadingPublicApiKey(true)
        let res = await server.getDeveloperHubConfiguration(client_id, service_id, email, 'developer-hub', 'configuration')
        if(res.status === 200){
            let resp = await res.json();
            setPrivateApiKey(resp.data.private_api_key || null);
            setPublicApiKey(resp.data.public_api_key || null);
            setCallAnalytics(normalizeCallbackConfigurations(resp.data.call_analytics));
            setChatAnalytics(normalizeCallbackConfigurations(resp.data.chat_analytics));
            setVoiceAgentsAnalytics(normalizeCallbackConfigurations(resp.data.voice_agents_analytics));
            setLoadingPrivateApiKey(false)
            setLoadingPublicApiKey(false)
        }
    } catch (error) {
        setLoadingPrivateApiKey(false)
        setLoadingPublicApiKey(false)
    }
  }

  const saveWidgetConfiguration = async () => {
    const validationError = validateWidgetConfiguration(widgetForm)
    if(validationError){
      showToast(validationError, 'error')
      return
    }

    const sanitizedWidget = sanitizeWidgetConfiguration(widgetForm)

    const action = widgetModalMode === 'create' ? 'create' : 'update'
    const config = {
      client_id,
      service_id,
      email,
      menu: 'developer-hub',
      submenu: 'configuration',
      action,
      widget_id: sanitizedWidget.widget_id,
      widget_title: sanitizedWidget.widget_title,
      widget_configuration: sanitizedWidget
    }

    try {
      setSavingWidgetConfiguration(true)
      let res = await server.saveDeveloperHubWidgetConfiguration(config)
      let resp = await res.json();
      if(res.status === 200){
        closeWidgetModal()
        getWidgetConfigurations(client_id, service_id, email)
        showToast('Widget configuration saved successfully', 'success')
      }else{
        showToast(resp.error || 'Unable to save widget configuration', 'error')
      }
      setSavingWidgetConfiguration(false)
    } catch (error) {
      setSavingWidgetConfiguration(false)
      showToast('Unable to save widget configuration', 'error')
    }
  }

  const deleteWidgetConfiguration = async () => {
    const widget = widgetToDelete
    if(!widget){
      return
    }
    const widgetId = widget.widget_id || widget.id
    if(!widgetId){
      showToast('Widget ID is missing.', 'error')
      return
    }
    try {
      setDeletingWidgetId(widgetId)
      let res = await server.saveDeveloperHubWidgetConfiguration({
        client_id,
        service_id,
        email,
        menu: 'developer-hub',
        submenu: 'configuration',
        action: 'delete',
        widget_id: widgetId
      })
      let resp = await res.json();
      if(res.status === 200){
        showToast('Widget configuration deleted successfully', 'success')
        setWidgetToDelete(null)
        getWidgetConfigurations(client_id, service_id, email)
      }else{
        showToast(resp.error || 'Unable to delete widget configuration', 'error')
      }
      setDeletingWidgetId(null)
    } catch (error) {
      setDeletingWidgetId(null)
      showToast('Unable to delete widget configuration', 'error')
    }
  }

  const isValidUrl = (url) => {
    try {
      if(!url) return true;
        const parsedUrl = new URL(url);
        return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch (error) {
        return false;
    }
  }

  const saveDeveloperHubConfiguration = async (type, callbacksOverride = null) => {
    const { callbacks } = getAnalyticsStateHelpers(type)
    const callbacksToSave = callbacksOverride || callbacks
    const sanitizedCallbacks = callbacksToSave
      .filter(callback => callback.url?.trim() || normalizeWebhookHeaders(callback.header).trim())
      .map(callback => {
        const targetKey = type === 'voice_agents' ? 'agent_ids' : 'service_ids'
        return {
          callback_id: callback.callback_id || callback.id,
          callback_url: callback.url?.trim() || '',
          callback_header: normalizeWebhookHeaders(callback.header).trim(),
          [targetKey]: getCallbackTargetIds(callback)
        }
      })

    const incompleteCallback = sanitizedCallbacks.find(callback => !callback.callback_url && callback.callback_header)
    if(incompleteCallback){
        showToast('Callback URL is required when callback headers are added.', 'error')
        return false
    }

    const invalidCallback = sanitizedCallbacks.find(callback => !isValidUrl(callback.callback_url))
    if(invalidCallback){
        showToast('Callback URL is not valid. Please enter a valid URL.', 'error')
        return false
    }

    let config = {
        'client_id': client_id,
        'service_id': service_id,
        'email': email,
        'menu': 'developer-hub',
        'submenu': 'configuration',
        'analytics_type': type,
        'analytics_callbacks': sanitizedCallbacks
    }
    try {
      let res = await server.saveDeveloperHubConfiguration(config)
      let resp = await res.json();
      if(res.status === 200){
          showToast('Configuration saved successfully', 'success')
          return true
      }else{
        showToast(resp.error, 'error')
        return false
      }
    } catch (error) {
      showToast('Unable to save callback configuration', 'error')
      return false
    }
  }

  useEffect(()=>{
    if(clientID && serviceID){
      setClient_id(clientID)
      setService_id(serviceID)
      const user = getCookie('dashboard_login_user_creds')
      if(user){
        const userData = JSON.parse(user)
        const email = userData.email
        setEmail(email)
      }
    }
  },[clientID, serviceID])

  useEffect(()=> {
    if(client_id && service_id && email){
      getDeveloperHubConfiguration(client_id, service_id, email)
      getMcpConfiguration(client_id, service_id, email)
      getWidgetConfigurations(client_id, service_id, email)
      getServiceIdOptions(client_id, service_id, email)
      getVoiceAgentOptions(client_id, service_id, email)
    }
  }, [client_id, service_id, email, getMcpConfiguration, getWidgetConfigurations, getServiceIdOptions, getVoiceAgentOptions])

  const mcpControlsDisabled = loadingMcpAccess || !mcpFeatureEnabled
  const formatMcpTrialExpiry = (epochSeconds) => {
    if(!epochSeconds) return null
    const date = new Date(Number(epochSeconds) * 1000)
    if(Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    })
  }
  const mcpTrialExpiryLabel = formatMcpTrialExpiry(mcpTrialAccessExpiresAt)
  const showMcpTrialActiveBanner = mcpTrialAccessEnabled && mcpTrialAccessActive && !mcpPermanentFeatureEnabled && mcpTrialExpiryLabel
  const showMcpTrialExpiredBanner = mcpTrialAccessEnabled && !mcpTrialAccessActive && !mcpPermanentFeatureEnabled && mcpTrialExpiryLabel
  const callbackTargetOptions = {
    call: [{ value: ALL_CALLBACK_TARGET, label: 'All service IDs' }, ...serviceIdOptions],
    chat: [{ value: ALL_CALLBACK_TARGET, label: 'All service IDs' }, ...serviceIdOptions],
    voice_agents: [{ value: ALL_CALLBACK_TARGET, label: 'All agent IDs' }, ...voiceAgentOptions]
  }
  const callbackTypeLabels = {
    call: 'Call Analytics',
    chat: 'Chat Analytics',
    voice_agents: 'Voice Agents'
  }
  const callbackPlaceholders = {
    call: 'https://your-api.com/call-webhook',
    chat: 'https://your-api.com/chat-webhook',
    voice_agents: 'https://your-api.com/voice-agents-webhook'
  }
  const getCallbackTargetLabel = (type, callback) => {
    const normalizedTargetIds = getCallbackTargetIds(callback)
    return normalizedTargetIds
      .map(targetId => {
        const targetOption = callbackTargetOptions[type]?.find(option => option.value === targetId)
        return targetOption?.label || targetId
      })
      .join(', ')
  }

  const renderCallbackSection = ({
    type,
    title,
    expanded,
    setExpanded,
    callbacks,
    placeholder,
    showHeaderInfo,
    setShowHeaderInfo,
    targetLabel
  }) => (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className={`${expanded ? 'px-6 py-4 border-b border-gray-100' : 'px-6 py-4'} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex min-w-0 items-center gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400" />
          )}
          <h3 className="text-base font-medium text-gray-900">{title}</h3>
        </button>
      </div>

      {expanded && (
        <>
        <div className="px-6 py-3 flex justify-end">
          <button
            onClick={() => openCreateCallbackModal(type)}
            className="inline-flex items-center justify-center px-3 py-1.5 rounded border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-4 h-4 mr-1.5 text-gray-500" />
            Add Callback
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border-t border-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{targetLabel}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="relative inline-flex items-center gap-2">
                    <span>Header</span>
                    <Info className="w-4 h-4 text-gray-400" onMouseEnter={()=>setShowHeaderInfo(true)} onMouseLeave={()=>setShowHeaderInfo(false)} />
                    {showHeaderInfo && <div className="absolute left-16 top-5 z-10 w-64 rounded-md border bg-white p-2 text-xs font-normal normal-case tracking-normal text-gray-500 shadow-md">
                        <p>
                            You can add multiple headers by separating them with a semicolon.
                        </p>
                    </div>}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {callbacks.map((callback) => (
                <tr key={callback.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {getCallbackTargetLabel(type, callback)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 break-all">
                    {callback.url || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 break-all">
                    {normalizeWebhookHeaders(callback.header) || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => openEditCallbackModal(type, callback)}
                          className="text-indigo-600 hover:underline"
                          title="Edit callback"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteCallbackConfirmation(type, callback)}
                          disabled={callbacks.length === 1 && !callback.url && !normalizeWebhookHeaders(callback.header)}
                          className="text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                          title="Remove callback"
                        >
                          Delete
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 py-3 px-5 font-sans">
      {(role === 'admin' || role === 'superadmin') && <div className="max-w-7xl mx-auto space-y-6">
        <div className='mb-8'>
            <h1 className="text-xl font-semibold text-gray-900 mb-2"><span className='text-gray-500'>Developer Hub &gt;</span><span className='text-gray-900'> Configuration</span></h1>
            <p className="text-gray-500 text-sm">Manage callbacks, API keys, MCP access, events, and client widgets</p>
        </div>

        <div className="flex space-x-6 text-gray-600">
          {DEVELOPER_HUB_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm transition-colors ${activeTab === tab.id ? 'bg-gray-100 text-gray-800 font-medium rounded' : 'hover:text-gray-900'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'api_keys' && <>
        {/* API Key Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">API Key</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Private API Key
              </label>
              {!loadingPrivateApiKey && <div className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type={showPrivateApiKey || !privateApiKey ? "text" : "password"}
                    value={privateApiKey || 'No API Key created yet. Please generate'}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm focus:outline-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => setShowPrivateApiKey(!showPrivateApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPrivateApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={() => copyToClipboardPrivateApiKey(privateApiKey)}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Copy API Key"
                >
                  {!showCopiedPrivateApiKey ? <Copy className="w-4 h-4 text-gray-600" /> : <Check className="w-4 h-4 text-gray-600" />}
                </button>
                {showCopiedPrivateApiKey && <div className='absolute text-xs text-gray-500 right-10 mt-20'>API Key Copied!</div>}
                {!privateApiKey && <button
                  onClick={generateNewPrivateApiKey}
                  className="flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  <span>Generate</span>
                </button>}
              </div>}
              {loadingPrivateApiKey &&
                    <div className="flex justify-center items-center" style={{ height: "100%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                    </div>
                }
            </div>

            <div className="text-sm text-gray-500">
              Keep your API key secure and never share it publicly. Use this key to authenticate API requests.
            </div>
          </div>
          <div className="space-y-4">
            <div className='mt-4'>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Public API Key
              </label>
              {!loadingPublicApiKey && <div className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type={showPublicApiKey || !publicApiKey ? "text" : "password"}
                    value={publicApiKey || 'No API Key created yet. Please generate'}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm focus:outline-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => setShowPublicApiKey(!showPublicApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPublicApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={() => copyToClipboardPublicApiKey(publicApiKey)}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Copy API Key"
                >
                  {!showCopiedPublicApiKey ? <Copy className="w-4 h-4 text-gray-600" /> : <Check className="w-4 h-4 text-gray-600" />}
                </button>
                {showCopiedPublicApiKey && <div className='absolute text-xs text-gray-500 right-10 mt-20'>API Key Copied!</div>}
                {!publicApiKey && <button
                  onClick={generateNewPublicApiKey}
                  className="flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  <span>Generate</span>
                </button>}
              </div>}
              {loadingPublicApiKey &&
                    <div className="flex justify-center items-center" style={{ height: "100%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                    </div>
                }
            </div>
          </div>
        </div>
        </>}

        {activeTab === 'mcp' && <>
        {/* MCP Access Section */}
        <div className={`rounded-lg shadow-sm border p-6 ${mcpFeatureEnabled ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">MCP Access</h2>
              <p className="text-sm text-gray-500 mt-1">Connect external MCP clients to analytics data.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={MCP_SETUP_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <span>Setup Guide &amp; Starter Templates</span>
                <SquareArrowOutUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <button
                onClick={updateMcpEnabled}
                disabled={mcpControlsDisabled}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${mcpEnabled && mcpFeatureEnabled ? 'bg-indigo-600' : 'bg-gray-200'} ${mcpControlsDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                title={!mcpFeatureEnabled ? MCP_FEATURE_DISABLED_MESSAGE : mcpEnabled ? 'Disable MCP' : 'Enable MCP'}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${mcpEnabled && mcpFeatureEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          {!loadingMcpAccess && !mcpFeatureEnabled && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
              {showMcpTrialExpiredBanner && <div className="mb-1 font-medium text-gray-800">MCP trial access expired on {mcpTrialExpiryLabel}.</div>}
              {MCP_FEATURE_DISABLED_MESSAGE}
            </div>
          )}

          {!loadingMcpAccess && showMcpTrialActiveBanner && (
            <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
              MCP trial access expires on {mcpTrialExpiryLabel}.
            </div>
          )}

          {!loadingMcpAccess && <div className={`space-y-4 ${!mcpFeatureEnabled ? 'opacity-60' : ''}`}>
            <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MCP Server URL
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={mcpServerUrl || 'MCP server URL will appear after backend configuration'}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm focus:outline-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={() => copyToClipboardMcpServerUrl(mcpServerUrl)}
                      disabled={!mcpServerUrl || !mcpFeatureEnabled}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      title="Copy MCP Server URL"
                    >
                      {!showCopiedMcpServerUrl ? <Copy className="w-4 h-4 text-gray-600" /> : <Check className="w-4 h-4 text-gray-600" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MCP Client ID
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={mcpClientId || 'No MCP client ID created yet. Please generate'}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm focus:outline-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={() => copyToClipboardMcpClientId(mcpClientId)}
                      disabled={!mcpClientId || !mcpFeatureEnabled}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      title="Copy MCP Client ID"
                    >
                      {!showCopiedMcpClientId ? <Copy className="w-4 h-4 text-gray-600" /> : <Check className="w-4 h-4 text-gray-600" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MCP Client Secret
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type={showMcpClientSecret || !mcpClientSecret ? "text" : "password"}
                        value={mcpClientSecret || 'No MCP client secret created yet. Please generate'}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm focus:outline-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={() => setShowMcpClientSecret(!showMcpClientSecret)}
                        disabled={!mcpClientSecret || !mcpFeatureEnabled}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      >
                        {showMcpClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      onClick={() => copyToClipboardMcpClientSecret(mcpClientSecret)}
                      disabled={!mcpClientSecret || !mcpFeatureEnabled}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      title="Copy MCP Client Secret"
                    >
                      {!showCopiedMcpClientSecret ? <Copy className="w-4 h-4 text-gray-600" /> : <Check className="w-4 h-4 text-gray-600" />}
                    </button>
                    {!mcpClientId && <button
                      onClick={generateMcpCredential}
                      disabled={generatingMcpCredential || !mcpFeatureEnabled}
                      className="flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-60"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${generatingMcpCredential ? 'animate-spin' : ''}`} />
                      <span>Generate</span>
                    </button>}
                    {mcpClientId && <button
                      onClick={rotateMcpSecret}
                      disabled={rotatingMcpSecret || !mcpFeatureEnabled}
                      className="flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-60"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${rotatingMcpSecret ? 'animate-spin' : ''}`} />
                      <span>Rotate</span>
                    </button>}
                  </div>
                </div>
            </div>
          </div>}
          {loadingMcpAccess &&
            <div className="flex justify-center items-center" style={{ height: "100%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
          }
        </div>
        </>}

        {activeTab === 'callbacks' && <div className="space-y-3">
        {renderCallbackSection({
          type: 'call',
          title: 'Call Analytics Callback URLs',
          expanded: callExpanded,
          setExpanded: setCallExpanded,
          callbacks: callAnalytics,
          placeholder: 'https://your-api.com/call-webhook',
          showHeaderInfo: showHeaderInfoCall,
          setShowHeaderInfo: setShowHeaderInfoCall,
          targetLabel: 'Service ID'
        })}

        {renderCallbackSection({
          type: 'voice_agents',
          title: 'Voice Agents Callback URLs',
          expanded: voiceAgentsExpanded,
          setExpanded: setVoiceAgentsExpanded,
          callbacks: voiceAgentsAnalytics,
          placeholder: 'https://your-api.com/voice-agents-webhook',
          showHeaderInfo: showHeaderInfoVoiceAgents,
          setShowHeaderInfo: setShowHeaderInfoVoiceAgents,
          targetLabel: 'Agent ID'
        })}

        {renderCallbackSection({
          type: 'chat',
          title: 'Chat Analytics Callback URLs',
          expanded: chatExpanded,
          setExpanded: setChatExpanded,
          callbacks: chatAnalytics,
          placeholder: 'https://your-api.com/chat-webhook',
          showHeaderInfo: showHeaderInfoChat,
          setShowHeaderInfo: setShowHeaderInfoChat,
          targetLabel: 'Service ID'
        })}
        </div>}

        {activeTab === 'events' && (
          <ClientEventsConfiguration client_id={client_id} service_id={service_id} email={email} />
        )}

        {activeTab === 'widget' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Client Screen Widgets</h2>
                <p className="text-sm text-gray-500 mt-1">Manage call recording widgets shown on client screens.</p>
              </div>
              <button
                onClick={openCreateWidgetModal}
                className="inline-flex items-center justify-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Widget
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Widget ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Widget Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loadingWidgets && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">Loading widget configurations...</td>
                    </tr>
                  )}
                  {!loadingWidgets && widgets.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">No widget configurations found.</td>
                    </tr>
                  )}
                  {!loadingWidgets && widgets.map((widget) => (
                    <tr key={widget.widget_id || widget.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-700">{widget.widget_id || widget.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{widget.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{widget.updated_by || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{formatWidgetUpdatedAt(widget.updated_at)}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => openEditWidgetModal(widget)}
                            className="text-indigo-600 hover:underline"
                            title="Edit widget"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setWidgetToDelete(widget)}
                            disabled={deletingWidgetId === (widget.widget_id || widget.id)}
                            className="text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                            title="Delete widget"
                          >
                            {deletingWidgetId === (widget.widget_id || widget.id) ? 'Deleting' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>}
      { (role === 'employee' || role === 'manager') && (
        <UnauthorizedPage/>)
      }
      {callbackModalOpen && callbackModalType && (
        <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative bg-white rounded-lg shadow-xl border w-full max-w-2xl mx-4 overflow-visible">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {callbackModalMode === 'create' ? 'Add' : 'Edit'} {callbackTypeLabels[callbackModalType]} Callback
                </h2>
                <p className="text-sm text-gray-500 mt-1">Configure the callback target, URL, and headers.</p>
              </div>
              <button
                onClick={closeCallbackModal}
                className="inline-flex items-center justify-center text-slate-500 hover:text-slate-700"
                title="Close"
                disabled={savingCallbackConfiguration}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {callbackModalType === 'voice_agents' ? 'Agent IDs' : 'Service IDs'}
                </label>
                <ModernTimeDropdown
                  width="w-full"
                  height="h-10"
                  placeholder={`Select ${callbackModalType === 'voice_agents' ? 'Agent IDs' : 'Service IDs'}`}
                  value={getCallbackTargetDropdownValue(callbackModalType, callbackForm)}
                  onChange={(e) => handleCallbackTargetDropdownChange(callbackModalType, e.target.value)}
                  options={getCallbackTargetDropdownOptions(callbackModalType)}
                  isMulti
                  showBulkActions
                  isDisabled={callbackModalType === 'voice_agents' && loadingVoiceAgentOptions}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL</label>
                <input
                  type="url"
                  value={callbackForm.url || ''}
                  onChange={(e) => updateCallbackForm('url', e.target.value)}
                  placeholder={callbackPlaceholders[callbackModalType]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 relative">
                  Header
                  <Info className="w-4 h-4 text-gray-400" onMouseEnter={()=>setShowHeaderInfoCall(true)} onMouseLeave={()=>setShowHeaderInfoCall(false)} />
                  {showHeaderInfoCall && <div className="absolute left-20 top-5 z-10 w-64 rounded-md border bg-white p-2 text-xs text-gray-500 shadow-md">
                    <p>You can add multiple headers by separating them with a semicolon.</p>
                  </div>}
                </label>
                <input
                  type="text"
                  value={normalizeWebhookHeaders(callbackForm.header)}
                  onChange={(e) => updateCallbackForm('header', e.target.value)}
                  placeholder="Authorization: Bearer your-token (semicolon separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeCallbackModal}
                disabled={savingCallbackConfiguration}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveCallbackModalConfiguration}
                disabled={savingCallbackConfiguration}
                className="inline-flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingCallbackConfiguration ? 'Saving...' : 'Save Callback'}
              </button>
            </div>
          </div>
        </div>
      )}
      {callbackToDelete && (
        <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 pb-2">
              <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Delete callback?</h2>
              <button
                onClick={() => setCallbackToDelete(null)}
                className="text-gray-500 hover:text-gray-700"
                disabled={deletingCallbackConfiguration}
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            <div className="p-6 pt-2 space-y-2">
              <p className="text-gray-500 text-sm">
                This will remove the {callbackTypeLabels[callbackToDelete.type]} callback and save the updated configuration.
              </p>
              <p className="text-xs text-gray-500 break-all">
                {callbackToDelete.callback?.url || 'No callback URL configured'}
              </p>
            </div>

            <div className="flex justify-end gap-3 p-6 pt-0">
              <button
                onClick={() => setCallbackToDelete(null)}
                disabled={deletingCallbackConfiguration}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                style={{height: "40px", borderRadius: "4px"}}
              >
                No
              </button>
              <button
                onClick={deleteCallbackConfiguration}
                disabled={deletingCallbackConfiguration}
                className={`px-6 py-2 rounded-lg bg-indigo-600 text-white ${deletingCallbackConfiguration ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                style={{height: "40px", borderRadius: "4px"}}
              >
                {deletingCallbackConfiguration ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {widgetToDelete && (
        <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 pb-2">
              <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Delete widget?</h2>
              <button
                onClick={() => setWidgetToDelete(null)}
                className="text-gray-500 hover:text-gray-700"
                disabled={Boolean(deletingWidgetId)}
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            <div className="p-6 pt-2">
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete {widgetToDelete.title ? `"${widgetToDelete.title}"` : 'this widget configuration'}?
              </p>
            </div>

            <div className="flex justify-end gap-3 p-6 pt-0">
              <button
                onClick={() => setWidgetToDelete(null)}
                disabled={Boolean(deletingWidgetId)}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                style={{height: "40px", borderRadius: "4px"}}
              >
                No
              </button>
              <button
                onClick={deleteWidgetConfiguration}
                disabled={Boolean(deletingWidgetId)}
                className={`px-6 py-2 rounded-lg bg-indigo-600 text-white ${deletingWidgetId ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                style={{height: "40px", borderRadius: "4px"}}
              >
                {deletingWidgetId ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {widgetModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="relative bg-white rounded-lg shadow-xl border w-full max-w-4xl h-[82vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{widgetModalMode === 'create' ? 'Add Widget Configuration' : 'Edit Widget Configuration'}</h2>
                  <p className="text-sm text-gray-500 mt-1">Configure recording fields, webhook delivery, and optional Conversation Analytics processing.</p>
                </div>
                <button
                  onClick={closeWidgetModal}
                  className="inline-flex items-center justify-center text-slate-500 hover:text-slate-700"
                  title="Close"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>

              {loadingWidgetDetails ? (
                <div className="flex-1 min-h-0 px-6 py-12 text-center text-sm text-gray-500">Loading widget details...</div>
              ) : (
                <div className="flex-1 min-h-0 p-6 overflow-y-auto space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input
                        type="text"
                        value={widgetForm.title}
                        onChange={(e) => updateWidgetForm('title', e.target.value)}
                        placeholder="Call Recording Widget"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Duration (seconds)</label>
                      <input
                        type="number"
                        min="1"
                        value={widgetForm.max_duration_seconds}
                        onChange={(e) => updateWidgetForm('max_duration_seconds', e.target.value)}
                        placeholder="300"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Widget Webhooks</h3>
                        <p className="text-xs text-gray-500 mt-1">Headers support multiple values separated by semicolon.</p>
                      </div>
                      <button
                        onClick={addWidgetFormWebhook}
                        className="inline-flex items-center px-3 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Webhook
                      </button>
                    </div>

                    {widgetForm.widget_webhooks.map((webhook, webhookIndex) => (
                      <div key={webhook.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_42px] gap-3 items-end border border-gray-200 rounded-lg p-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL {webhookIndex + 1}</label>
                          <input
                            type="url"
                            value={webhook.webhook_url}
                            onChange={(e) => updateWidgetFormWebhook(webhook.id, 'webhook_url', e.target.value)}
                            placeholder="https://your-api.com/widget-recording-webhook"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Headers</label>
                          <input
                            type="text"
                            value={webhook.webhook_header || webhook.headers || ''}
                            onChange={(e) => updateWidgetFormWebhook(webhook.id, 'webhook_header', e.target.value)}
                            placeholder="Authorization: Bearer your-token"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <button
                          onClick={() => removeWidgetFormWebhook(webhook.id)}
                          disabled={widgetForm.widget_webhooks.length === 1}
                          className="h-10 w-10 inline-flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove webhook"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Expected Fields</h3>
                      <button
                        onClick={addWidgetFormField}
                        className="inline-flex items-center px-3 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Field
                      </button>
                    </div>

                    {widgetForm.fields.map((field) => (
                      <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_140px_110px_42px] gap-3 items-end border border-gray-200 rounded-lg p-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                          <input
                            type="text"
                            value={field.name}
                            onChange={(e) => updateWidgetFormField(field.id, 'name', e.target.value)}
                            placeholder="Customer Name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => updateWidgetFormField(field.id, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500 bg-white"
                            >
                              <option value="text">Text</option>
                              <option value="number">Number</option>
                              <option value="dropdown">Dropdown</option>
                              <option value="textarea">Textarea</option>
                            </select>
                          </div>
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                            <input
                              type="checkbox"
                              checked={Boolean(field.required)}
                              onChange={(e) => updateWidgetFormField(field.id, 'required', e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Required
                          </label>
                          <button
                            onClick={() => removeWidgetFormField(field.id)}
                            disabled={widgetForm.fields.length === 1}
                            className="h-10 w-10 inline-flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(widgetForm.send_to_call_analytics)}
                        onChange={(e) => updateWidgetForm('send_to_call_analytics', e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">Send recording to Conversation Analytics</span>
                        <span className="block text-xs text-gray-600 mt-1">The saved recording will use the existing batch analysis flow and its configured completion callback.</span>
                      </span>
                    </label>

                    {widgetForm.send_to_call_analytics && (
                      <div className="space-y-4 border-t border-indigo-100 pt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Conversation Analytics Project</label>
                          <select
                            value={widgetForm.analytics_service_id}
                            onChange={(e) => updateWidgetForm('analytics_service_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500 bg-white"
                          >
                            <option value="">Select a project</option>
                            {serviceIdOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <div className="mb-3">
                            <h3 className="text-sm font-semibold text-gray-900">Field Mapping</h3>
                            <p className="text-xs text-gray-600 mt-1">Map widget fields to the standard analysis fields. Record ID falls back to the generated conversation ID.</p>
                          </div>
                          <div className="space-y-4">
                            {WIDGET_ANALYTICS_FIELD_GROUPS.map(group => (
                              <div key={group.label}>
                                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {group.fields.map(field => (
                                    <div key={field.key} className="rounded-lg border border-gray-200 bg-white p-3">
                                      <label className="mb-2 flex items-center justify-between gap-2 text-xs font-medium text-gray-700">
                                        <span>{field.label}</span>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${field.required ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                          {field.required ? 'Mandatory' : 'Optional'}
                                        </span>
                                      </label>
                                      <select
                                        value={widgetForm.analytics_field_mapping?.[field.key] || ''}
                                        onChange={(e) => updateWidgetAnalyticsMapping(field.key, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500 bg-white"
                                      >
                                        <option value="">{field.key === 'id' ? 'Use generated conversation ID' : 'Not mapped'}</option>
                                        {widgetForm.fields.filter(widgetField => widgetField.name?.trim()).map(widgetField => (
                                          <option key={`${field.key}_${widgetField.id}`} value={widgetField.name.trim()}>{widgetField.name.trim()}</option>
                                        ))}
                                      </select>
                                      {field.description && <p className="mt-1.5 text-[11px] text-gray-500">{field.description}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 p-3">
                              <p className="text-xs font-semibold text-emerald-900">Set automatically by Anthropod</p>
                              <p className="mt-1 text-xs text-emerald-800">Recording file, Call time (recording start in epoch seconds), and Call answer status (Connected).</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Fields Input Mode</label>
                      <select
                        value={widgetForm.source_type}
                        onChange={(e) => updateWidgetForm('source_type', e.target.value)}
                        className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="url">URL Based</option>
                        <option value="manual">Manual Input</option>
                      </select>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(widgetForm.active)}
                        onChange={(e) => updateWidgetForm('active', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Active
                    </label>
                  </div>

                  {widgetForm.source_type === 'url' && <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Configuration URL</label>
                      <input
                        type="url"
                        value={widgetForm.source_url}
                        onChange={(e) => updateWidgetForm('source_url', e.target.value)}
                        placeholder="https://your-api.com/widget-fields"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">The widget will fetch field definitions from this URL.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Configuration Header</label>
                      <input
                        type="text"
                        value={widgetForm.source_header}
                        onChange={(e) => updateWidgetForm('source_header', e.target.value)}
                        placeholder="Authorization: Bearer your-token (semicolon separated)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-indigo-500 focus:border-indigo-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">You can add multiple headers by separating them with a semicolon.</p>
                    </div>
                  </div>}

                </div>
              )}

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={closeWidgetModal}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveWidgetConfiguration}
                  disabled={savingWidgetConfiguration || loadingWidgetDetails}
                  className="inline-flex items-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-60"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingWidgetConfiguration ? 'Saving...' : 'Save Widget'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}

export default DeveloperHubConfiguration;
